import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  LEAGUE_NAME_MAX_LENGTH,
  LEAGUE_MOTTO_MAX_LENGTH,
  validateLeagueName,
  validateLeagueMotto,
} from '../nameValidation'
import JoinPolicyChoice from './leagueJoinPolicy/JoinPolicyChoice'
import type { LeagueJoinPolicy } from '../types'
import './CreateLeagueScreen.css'

interface CreateLeagueScreenProps {
  onCreate: (name: string, motto: string, joinPolicy: LeagueJoinPolicy) => Promise<void> | void
  onCancel: () => void
}

function CreateLeagueScreen({ onCreate, onCancel }: CreateLeagueScreenProps) {
  const [name, setName] = useState('')
  const [motto, setMotto] = useState('')
  // Ask to join by default: findable, but nobody gets in without the owner saying
  // so. Open is a bigger promise than a default should make on someone's behalf.
  const [joinPolicy, setJoinPolicy] = useState<LeagueJoinPolicy>('request')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()

    const result = validateLeagueName(trimmed)
    if (!result.valid) {
      setError(result.error)
      return
    }

    // Motto is optional — an empty field is fine.
    const mottoResult = validateLeagueMotto(motto)
    if (!mottoResult.valid) {
      setError(mottoResult.error)
      return
    }

    setError(null)
    setIsSubmitting(true)
    try {
      await onCreate(trimmed, motto, joinPolicy)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="create-league" onSubmit={handleSubmit}>
      <p className="create-league__title">Create a league</p>
      <input
        className="create-league__input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="League name"
        maxLength={LEAGUE_NAME_MAX_LENGTH}
        autoFocus
      />
      <input
        className="create-league__input"
        value={motto}
        onChange={(e) => setMotto(e.target.value)}
        placeholder="Say something (optional)"
        maxLength={LEAGUE_MOTTO_MAX_LENGTH}
      />
      <div className="create-league__policy">
        <p className="create-league__policy-label label-plain">Who can join</p>
        <JoinPolicyChoice name="create-league-policy" value={joinPolicy} onChange={setJoinPolicy} />
      </div>
      {error && <p className="create-league__error">{error}</p>}
      <div className="create-league__actions">
        <button
          type="button"
          className="create-league__cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="create-league__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  )
}

export default CreateLeagueScreen
